import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { apiGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Music, LogIn } from "lucide-react";
import { toast } from "sonner";

const DEMO_USERS = [
    { email: "owner@musook.studio", label: "Studio Owner", role: "owner" },
    { email: "manager@musook.studio", label: "General Manager", role: "manager" },
    { email: "booking@musook.studio", label: "Booking Manager", role: "booking_manager" },
    { email: "frontdesk@musook.studio", label: "Front Desk", role: "front_desk" },
    { email: "mixer@musook.studio", label: "Mixing Engineer", role: "mixing_engineer" },
    { email: "engineer@musook.studio", label: "Engineer", role: "engineer" },
    { email: "accountant@musook.studio", label: "Accountant", role: "accountant" },
    { email: "luna@artist.demo", label: "Client (Artist)", role: "client" },
];

export default function LoginPage() {
    const { user, login, devLoginAs } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/dashboard";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [demoMode, setDemoMode] = useState(null);

    useEffect(() => {
        apiGet("/health")
            .then((health) => {
                const isDemo = health?.data_mode === "demo";
                setDemoMode(isDemo);
                if (isDemo) {
                    setEmail((value) => value || "owner@musook.studio");
                    setPassword((value) => value || "musook123");
                }
            })
            .catch(() => setDemoMode(false));
    }, []);

    useEffect(() => {
        if (user) navigate(from, { replace: true });
    }, [user, navigate, from]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login({ email, password });
            toast.success("Welcome back");
            navigate(from, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    const quickLogin = async (u) => {
        setSubmitting(true);
        try {
            await devLoginAs(u.email);
            toast.success(`Signed in as ${u.label}`);
            navigate("/dashboard", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Demo sign-in is unavailable for this environment");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[hsl(var(--paper))]">
            {/* Left — hero */}
            <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-[hsl(var(--paper))] via-white to-[hsl(var(--muted))] relative overflow-hidden">
                <div className="paper-noise absolute inset-0 opacity-40 pointer-events-none" />
                <div className="relative z-10 flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center text-primary-foreground">
                        <Music className="h-6 w-6" strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className="font-display text-2xl font-semibold tracking-tight">Musook</div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Studio Operating System</div>
                    </div>
                </div>
                <div className="relative z-10 max-w-lg">
                    <h1 className="font-display text-4xl md:text-5xl leading-tight font-semibold tracking-tight">
                        The calm way to run a great studio.
                    </h1>
                    <p className="mt-4 text-base text-muted-foreground">
                        Bookings, contacts, projects, invoices and analytics — one warm, focused workspace for studios that live and breathe records.
                    </p>
                    <ul className="mt-8 space-y-3 text-sm">
                        {[
                            "Multi-room scheduling with conflict-safe bookings",
                            "Conversational AI booking agent + guided wizard",
                            "Client 360° + pipeline that tells staff the next action",
                            "Projects, revisions, deliverables from intake to delivery",
                        ].map((t) => (
                            <li key={t} className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="relative z-10 text-xs text-muted-foreground">
                    Trusted by boutique and label-owned studios — handcrafted for engineers, artists and owners.
                </div>
            </div>

            {/* Right — form */}
            <div className="flex flex-col justify-center p-6 md:p-12">
                <Card className="mx-auto w-full max-w-md border-border shadow-lg" data-testid="login-card">
                    <CardContent className="p-6 md:p-8">
                        <div className="flex items-center gap-2 lg:hidden mb-6">
                            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
                                <Music className="h-5 w-5" strokeWidth={1.75} />
                            </div>
                            <span className="font-display text-xl font-semibold">Musook</span>
                        </div>
                        <h2 className="font-display text-2xl font-semibold tracking-tight">Welcome back</h2>
                        <p className="text-sm text-muted-foreground mt-1">Sign in to run your studio.</p>
                        {demoMode === false && (
                            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="live-login-notice">
                                This is the live workspace. Use the owner credentials created for this environment; demo accounts are available only in the demo workspace.
                            </p>
                        )}

                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Work email</Label>
                                <Input
                                    id="email"
                                    data-testid="login-email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    data-testid="login-password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={submitting} data-testid="login-submit">
                                <LogIn className="mr-2 h-4 w-4" /> Sign in
                            </Button>
                        </form>
                        <p className="mt-5 text-center text-sm text-muted-foreground">New studio? <Link className="font-semibold text-primary hover:underline" to="/signup">Create an owner account</Link></p>

                        {demoMode && (
                            <div className="mt-6">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Demo accounts (dev)</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {DEMO_USERS.map((u) => (
                                        <button
                                            key={u.email}
                                            onClick={() => quickLogin(u)}
                                            disabled={submitting}
                                            data-testid={`demo-login-${u.role}`}
                                            className="text-left rounded-md border border-border bg-card/70 hover:bg-card px-2.5 py-1.5 text-xs shadow-sm hover:shadow transition-shadow"
                                        >
                                            <div className="font-medium">{u.label}</div>
                                            <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
